import { z } from 'zod';

export const uuidSchema = z.string().uuid();

export const moneyStringSchema = z
  .string()
  .regex(/^-?\d+(\.\d{1,4})?$/, 'must be a decimal with up to 4 fractional digits');

/**
 * Money that may be zero but never negative — prices, PV, grants, dimensions.
 * A ₱0 product is legitimate (free-slot kits, samples); a negative one never is.
 *
 * Expressed as a REGEX rather than `moneyStringSchema.refine(...)` on purpose:
 * `.refine` yields a ZodEffects, and a ZodEffects nested inside an object that
 * is later `.partial()`-ed or checked with `satisfies` degrades zod's output
 * inference to `unknown`. Staying a plain ZodString keeps every derived schema
 * (createProductSchema.partial(), houseVariantUpdateSchema, …) correctly typed.
 */
export const nonNegativeMoneySchema = z
  .string()
  .regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative decimal with up to 4 fractional digits');

/**
 * Money that must be strictly positive. Use for any amount that is CHARGED or
 * MOVED: per-currency sticker prices (`price_gc`/`price_coin`), absolute segment
 * price overrides, top-up/payment amounts.
 *
 * A stored `0` here is what let a ₱50,000 kit be bought with GC for nothing —
 * the old checkout gate rejected only NULL, so `0` read as a valid free price.
 * NULL means "not sold in this currency"; `0` is never a real price.
 *
 * The leading negative lookahead rejects every all-zero spelling — `0`, `00`,
 * `0.0`, `0.0000` — while accepting `0.0001`. See zod/common.test.ts.
 */
export const positiveMoneySchema = z
  .string()
  .regex(
    /^(?!0+(\.0{1,4})?$)\d+(\.\d{1,4})?$/,
    'must be a decimal greater than 0 with up to 4 fractional digits',
  );

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(20),
});

export type Pagination = z.infer<typeof paginationSchema>;

export const isoDateSchema = z.string().datetime({ offset: true });

/** Structured shipping/contact address. `regionCode` drives shipping rates. */
export const addressSchema = z.object({
  line1: z.string().max(200).optional(),
  line2: z.string().max(200).optional(),
  barangay: z.string().max(120).optional(),
  city: z.string().max(120).optional(),
  province: z.string().max(120).optional(),
  region: z.string().max(120).optional(),
  regionCode: z.string().max(12).optional(),
  // JM-EX4: PSGC-style codes captured by the cascading dropdown (phil-reg-prov-mun-brgy).
  // Optional and additive — pre-existing addresses (name-only) keep validating.
  provinceCode: z.string().max(12).optional(),
  cityCode: z.string().max(12).optional(),
  barangayCode: z.string().max(12).optional(),
  postalCode: z.string().max(20).optional(),
  contactNo: z.string().max(40).optional(),
  // Merchant FF delivery extras (couriers need them): the person the rider asks
  // for, and a free-text full address the merchant types out. Optional + additive.
  contactPerson: z.string().max(160).optional(),
  fullAddress: z.string().max(500).optional(),
});

export type Address = z.infer<typeof addressSchema>;

/**
 * Strict address for merchant FF delivery: every courier-required field must be
 * present (PSGC region→barangay, line 1, postal, delivery contact # + person,
 * and the free-text full address). Built by refining {@link addressSchema} so
 * the optional shape stays the source of truth.
 */
export const requiredDeliveryAddressSchema = addressSchema.superRefine((a, ctx) => {
  const required: Array<[keyof Address, string]> = [
    ['line1', 'Address line 1 is required'],
    ['regionCode', 'Region is required'],
    ['provinceCode', 'Province is required'],
    ['cityCode', 'City / Municipality is required'],
    ['barangayCode', 'Barangay is required'],
    ['postalCode', 'Postal code is required'],
    ['contactNo', 'Delivery contact # is required'],
    ['contactPerson', 'Delivery contact person is required'],
    ['fullAddress', 'Full address is required'],
  ];
  for (const [key, message] of required) {
    const v = a[key];
    if (v == null || String(v).trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message });
    }
  }
});

/**
 * A shipping address must carry a contact number so the courier can reach the
 * buyer on delivery. Returns true when the address has a non-blank contact
 * number. Used to gate physical-shipment order creation server-side (mirrors
 * the storefront's required-field check).
 */
export function shippingAddressHasContactNo(
  addr: { contactNo?: string | null } | null | undefined,
): boolean {
  return !!addr?.contactNo?.trim();
}
